import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, message, Pagination, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { fetchArchivedDieQuotationList, unArchiveDieQuotation } from '../../slices/die-quotation';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';


function ArchiveDieQuotationList() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState('');

  const dispatch = useDispatch()
  const debounceTimeout = useRef(null);
  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };
  const handleSearch = (e) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 50); // 300ms delay
  };
  useEffect(() => {
    dispatch(fetchArchivedDieQuotationList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
  }, [dispatch, page, pagesize, search, orderBy]);

  const { data = [], totalRecord = 0, status } = useSelector(state => state.dieQuotation || {});

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));
  const handleRestore = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(unArchiveDieQuotation(id)).unwrap()
      .then(() => {
        message.success('Die Quotation Unarchived Successfully!');
        dispatch(fetchArchivedDieQuotationList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "SrNo" && sortOrder === "ascend") {
      sortingEnumKey = "SrNo";
    }
    if (sortField === "SrNo" && sortOrder === "descend") {
      sortingEnumKey = "-SrNo";
    }
    if (sortField === "quotation_date" && sortOrder === "ascend") {
      sortingEnumKey = "quotation_date";
    }
    if (sortField === "quotation_date" && sortOrder === "descend") {
      sortingEnumKey = "-quotation_date";
    }
    if (sortField === "die_quotation_no" && sortOrder === "ascend") {
      sortingEnumKey = "die_quotation_no";
    }
    if (sortField === "die_quotation_no" && sortOrder === "descend") {
      sortingEnumKey = "-die_quotation_no";
    }
    if (sortField === "customer" && sortOrder === "ascend") {
      sortingEnumKey = "customer__customer_name";
    }
    if (sortField === "customer" && sortOrder === "descend") {
      sortingEnumKey = "-customer__customer_name";
    }
   
    return sortingEnumKey;
  };
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setOrderBy('')
  };
  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',

    },
    {
      title: 'Quotation Date',
      dataIndex: 'quotation_date',
      key: 'quotation_date',
      sorter: true,
      render: (quotation_date) => {
        return (
          <span>{quotation_date
            ? dayjs(quotation_date).format("DD-MM-YYYY")
            : "N/A"}</span>
        );
      },
    },
    {
      title: 'Quotation No.',
      dataIndex: 'die_quotation_no',
      key: 'die_quotation_no',
      sorter: true,
      render: (die_quotation_no) => {
        return (
          <span>{die_quotation_no ? die_quotation_no : 'N/A'}</span>
        );
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer',
      sorter: true,
      key: 'customer',
      render: (customer) => {
        return (
          <span>{customer?.customer_name ? customer.customer_name : 'N/A'}</span>
        );
      },
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
   
      render: (record) => {
        return record ? (
          <span>{dayjs(record).format('DD-MM-YYYY HH:mm')}</span>
        ) : (
          <span>N/A</span>
        );
      }
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: (record) => {
        return (
          <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'} </span>
        )
      }

    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to Unarchive this record?"
              onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
              onCancel={() => message.info('Action cancelled')} // Optional cancel action
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore className="ArchiverestorImg" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const onSearchChange = e => {
    setSearchText(e.target.value);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };
const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };
  return (
    <>
      <div className='sticyaction'>
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>

            <h2>Archive Die Quotations</h2>
            <div className="bredcrumbwrp">
              <Link to="/die-quotation-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: <Link to="/die-quotation-list">Die Quotations</Link> },
                  { title: 'Archive Die Quotations' }
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="middle">
              <Input
                placeholder="Search"
                  className='searchbox'
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}

                style={{ width: '100%' }}
              />
              <Link >
                <Tooltip title="Filter">
                  <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                    <FilterIcon />
                  </Button>                </Tooltip>
              </Link>
            </Space>
          </Col>
        </Row>
      </div>
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
      <Table
        //rowSelection={rowSelection}
        dataSource={dataSource}
        columns={columns}
        rowKey="id"
        // pagination={{
        //   defaultCurrent: page,
        //   defaultPageSize: pagesize,
        //   total: totalRecord,
        //   showTotal: (total) => `Total ${total} items`,
        //   showSizeChanger: true,
        //   onChange: handlePageChange,
        //   locale: {
        //     items_per_page: 'Items / Page', // Text for the size changer
        //   },
        // }} 
        pagination={false}
        onChange={handleTableChange}

        className="mt-5"
        scroll={{
          x: 880,
        }}
      />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
    </>
  );
}

export default ArchiveDieQuotationList;
